/**
* Nom : scientificArticleList.js
* Description : Ce fichier contient la définition de la page scientificArticleList 
*               Cette page liste les articles scientifiques issue de Mendeley avec un filtrage
* Date de création : 26 septembre 2024
*/

/* CSS et assets */
import { AddIcon } from 'package_ui_ihm/dist'
import { EditIcon } from 'package_ui_ihm/dist'
import { DeleteIcon } from 'package_ui_ihm/dist'
import { FileIcon } from 'package_ui_ihm/dist'

/* Bibliothèques et composants */
import { TabRD } from 'package_ui_ihm/dist'
import { Container } from 'package_ui_ihm/dist'
import { Item } from 'package_ui_ihm/dist'
import { InputFormSide } from 'package_ui_ihm/dist'
import { ListFilter } from 'package_ui_ihm/dist'
import { TextFilter } from 'package_ui_ihm/dist'
import { FileForm } from 'package_ui_ihm/dist'
import Divider from '@mui/material/Divider';
import { saveAs } from "file-saver";
import dayjs from 'dayjs';
import 'dayjs/locale/fr'; // <-- importe la locale

/* Hook */
import { useState, useEffect } from 'react';
import { useBlocker } from 'react-router-dom'
import { useAuth } from 'package_ui_ihm/dist'
import { useApi } from 'package_ui_ihm/dist'
import { useScreenWidth } from 'package_ui_ihm/dist'


// définit 'fr' comme locale globale
dayjs.locale('fr'); 


/**
* ScientificArticleList : affiche la page qui liste les articles scientifiques issue de Mendeley
*                         filtrage de la liste possible selon le DOI, le titre, l'auteur et l'année de publication
*                         un lien permet d'afficher un formulaire pour créer un nouvel article scientifique
*                         pour chaque article une icone permet de le supprimer
*
* Type : page 
*
* Parametres : 
*  None
*/
function ScientificArticleList() {

  // Define useState hooks

  const [articles, setArticles] = useState([])
  const [statusArticles, setStatusArticles] = useState(null)
  const [isDataLoading, setDataLoading] = useState(false)
  const [isCreation, setCreation] = useState(false)
  const [isUpdate, setUpdate] = useState(false)
  const [idUpdated, setIdUpdated] = useState(null)
  const [doi, setDoi] = useState(null);
  const [title, setTitle] = useState(null);
  const [author, setAuthor] = useState(null);
  const [publishedYear, setPublishedYear] = useState(null);
  const [keywords, setKeywords] = useState(null);
  const [newKeywords, setNewKeywords] = useState(null);
  const [doiError, setDoiError] = useState(false);
  const [titleError, setTitleError] = useState(false);
  const [authorError, setAuthorError] = useState(false);
  const [publishedYearError, setPublishedYearError] = useState(false);
  const [keywordsError, setKeywordsError] = useState(false);
  const [isFormClicked, setFormClicked] = useState(false);
  const [selectedDoi, setSelectedDoi] = useState("");
  const [selectedTitle, setSelectedTitle] = useState("");
  const [selectedAuthor, setSelectedAuthor] = useState("");
  const [selectedPublishedYear, setSelectedPublishedYear] = useState(0);
  const [selectedKeywords, setSelectedKeywords] = useState("");
  const [publishedYears, setPublishedYears] = useState([]);
  const [filteredTempArticles, setFilteredTempArticles] = useState([]);  
  const [filteredArticles, setFilteredArticles] = useState(articles); 
  const [file, setFile] = useState(null); 
  const [fileError, setFileError] = useState(false);
  const [statusImport, setStatusImport] = useState(null)
  const [lastDateImport, setLastDateImport] = useState('Aucune')


  // Define useContext hooks
  const { authStatus, authToken, currentUser } = useAuth();
  const { path } = useApi();


  // Define useBlocker hooks
  const shouldBlock = isCreation;
  const blocker = useBlocker(shouldBlock);


  // Define useScreenWidth hooks
  const screenWidth = useScreenWidth();


  // Define functions
  async function retrieveData() {
    // Token
    const token = "Bearer " + authToken
    // Headers
    const headers = {
      accept: 'application/json',
      Authorization: token,
    }
    // Date du dernier import
    const resImportDate = await fetch(path + "/api/last-import/", 
      {
        method: 'GET',
        credentials: 'include',
        mode: 'cors',
        headers,
      }
    );
    const dataImportDate = await resImportDate.json();
    setLastDateImport(dataImportDate["mendeley_last_import_date"])
    // Constantes de pagination
    const PAGE_SIZE = 100; 
    const CONCURRENCY = 3;
    // Articles
    try { 
      // Fonction pour récupérer une page 
      const fetchPage = async (page) => {
        const response = await fetch( 
          path + `/api/bo-scar/?page=${page}`, 
          { 
            method: "GET", 
            credentials: "include", 
            mode: "cors", 
            headers, 
          } 
        );
        if (!response.ok) { 
          throw new Error(`Erreur HTTP ${response.status} pour la page ${page}`); 
        }
        const data = await response.json(); 
        return { 
          page: page, 
          status: response.status, 
          results: data.results, 
          count: data.count, 
        }; 
      };
      // Première requête 
      const firstPage = await fetchPage(1); 
      setStatusArticles(firstPage.status);
      // Articles récupérés 
      const article_list = [...firstPage.results]; 
      // Nombre total de pages 
      const totalPages = Math.ceil(firstPage.count / PAGE_SIZE); 
      // Afficher immédiatement la première page 
      setArticles([...article_list]);
      setDataLoading(false); 
      // Récupération des pages suivantes par groupes de 3 
      for ( 
        let startPage = 2; 
        startPage <= totalPages; 
        startPage += CONCURRENCY 
      ) {
        const promises = []; 
        for ( 
          let page = startPage; 
          page < startPage + CONCURRENCY && page <= totalPages; 
          page++ 
        ) { 
          promises.push(fetchPage(page)); 
        }
        // Les 3 requêtes sont exécutées en parallèle 
        const pages = await Promise.all(promises); 
        // Ajouter les résultats dans l'ordre 
        pages 
          .sort((a, b) => a.page - b.page) 
          .forEach((pageData) => { 
            article_list.push(...pageData.results); 
          }); 
        // Mise à jour progressive de React 
        setArticles([...article_list]); 
      }
      // Published year des articles 
      const published_year_list = [ ...new Set( article_list.map((article) => article.published_year) ) ];
      const published_year_dict = published_year_list.map( (published_year, index) => ({ id: index + 1, value: published_year }) );
      setPublishedYears(published_year_dict);
    } catch (error) { 
      console.error("Erreur lors de la récupération des articles :", error); 
    } 
  }

  const handleClick = (event) => {
    event.preventDefault();
    setCreation(true)
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFormClicked(true)
    if(doi === null || doi === ''){
      setDoiError(true)
    }
    else
    {   
      setDoiError(false)
    }
    if(title === null || title === ''){
      setTitleError(true)
    }
    else
    {   
      setTitleError(false)
    }
    if(author === null || author === ''){
      setAuthorError(true)
    }
    else
    {   
      setAuthorError(false)
    }
    if(publishedYear === null || publishedYear === ''){
      setPublishedYearError(true)
    }
    else
    {   
      setPublishedYearError(false)
    }
    if(keywords === null || keywords === ''){
      setKeywordsError(true)
    }
    else
    {   
      setKeywordsError(false)
    }
  };

  const handleReset = (event) => {
    event.preventDefault();
    setCreation(false)
    setDoi(null)
    setTitle(null)
    setAuthor(null)
    setPublishedYear(null)
    setKeywords(null)
    setDoiError(false)
    setTitleError(false)
    setAuthorError(false)
    setPublishedYearError(false)
    setKeywordsError(false)
  };

  async function sendData(doi, title, author, publishedYear, keywords) {
    // Token
    const token = "Bearer " + authToken
    // Create scientific article
    await fetch(path + "/api/bo-scar/", 
      {
        method: 'POST',
        credentials: 'include',
        mode: 'cors',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
        },
        body: JSON.stringify({
            "doi": doi, 
            "title": title,
            "author": author,
            "published_year": Number(publishedYear),
            "abstract": " ",
            "keywords": keywords
        })
      }
    );
    setCreation(false)
    setDoi(null)
    setTitle(null)
    setAuthor(null)
    setPublishedYear(null)
    setKeywords(null)
    retrieveData()
    setFormClicked(false)
  }

  const handleEditIcon = (event, article) => {
    event.preventDefault();
    setIdUpdated(article['id'])
    setNewKeywords(article['keywords'])
    setUpdate(true)
  };
  
  const updateSubmit = (event) => {
    event.preventDefault();
    sendUpdate(newKeywords)
  };

  const updateReset = (event) => {
    event.preventDefault();
    setNewKeywords(null)
    setIdUpdated(null)
    setUpdate(false)
  };

  async function sendUpdate(keywords) {
    // Token
    const token = "Bearer " + authToken
    // update scientific article
    await fetch(path + "/api/bo-scar/" + idUpdated + "/", 
      {
        method: 'PATCH',
        credentials: 'include',
        mode: 'cors',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
        },
        body: JSON.stringify({
            "keywords": keywords
        })
      }
    );
    setNewKeywords(null)
    setIdUpdated(null)
    retrieveData()
    setUpdate(false)
  }

  const handleRemoveIcon = (event, id) => {
    event.preventDefault();
    sendDelete(id)
  };

  async function sendDelete(id) {
    // Token
    const token = "Bearer " + authToken
    // Delete scientific article
    await fetch(path + "/api/bo-scar/" + id + "/", 
      {
        method: 'DELETE',
        credentials: 'include',
        mode: 'cors',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
        }
      }
    );
    retrieveData()
  }

  function retrieve_param_object_from_id(list, id) {
    return (list.find((item) => (item['id'] === Number(id))))['value']
  }

  function updateFilteredArticles() {
    // Filter author
    if( selectedAuthor !== ""
        && Number(selectedPublishedYear) === 0
        && selectedDoi === ""
        && selectedTitle === "")
    {
      setFilteredTempArticles(articles.filter((article) => article.author.toLowerCase().includes(selectedAuthor.toLowerCase())))
    } 
    // Filter published year
    else if( selectedAuthor === "" 
              && Number(selectedPublishedYear) > 0
              && selectedDoi === ""
              && selectedTitle === "")
    {
      setFilteredTempArticles(articles.filter((article) => article.published_year === retrieve_param_object_from_id(publishedYears, selectedPublishedYear)))
    } 
    // Filter doi
    else if( selectedAuthor === "" 
              && Number(selectedPublishedYear) === 0
              && selectedDoi !== ""
              && selectedTitle === "")
    {
      setFilteredTempArticles(articles.filter((article) => article.doi.toLowerCase().includes(selectedDoi.toLowerCase())))
    } 
    // Filter title
    else if( selectedAuthor === "" 
              && Number(selectedPublishedYear) === 0
              && selectedDoi === ""
              && selectedTitle !== "")
    {
      setFilteredTempArticles(articles.filter((article) => article.title.toLowerCase().includes(selectedTitle.toLowerCase())))
    } 
    // Filter author and published year
    else if( selectedAuthor !== ""
              && Number(selectedPublishedYear) > 0
              && selectedDoi === ""
              && selectedTitle === "")
    {
      setFilteredTempArticles(articles.filter((article) => article.author.toLowerCase().includes(selectedAuthor.toLowerCase()) 
                                                        && article.published_year === retrieve_param_object_from_id(publishedYears, selectedPublishedYear)))
    } 
    // Filter author and doi
    else if( selectedAuthor !== ""
              && Number(selectedPublishedYear) === 0
              && selectedDoi !== ""
              && selectedTitle === "")
    {
      setFilteredTempArticles(articles.filter((article) => article.author.toLowerCase().includes(selectedAuthor.toLowerCase()) 
                                                        && article.doi.toLowerCase().includes(selectedDoi.toLowerCase())))
    } 
    // Filter author and title
    else if( selectedAuthor !== ""
              && Number(selectedPublishedYear) === 0
              && selectedDoi === ""
              && selectedTitle !== "")
    {
      setFilteredTempArticles(articles.filter((article) => article.author.toLowerCase().includes(selectedAuthor.toLowerCase()) 
                                                        && article.title.toLowerCase().includes(selectedTitle.toLowerCase())))
    } 
    // Filter published year and doi
    else if( selectedAuthor === ""
              && Number(selectedPublishedYear) > 0
              && selectedDoi !== ""
              && selectedTitle === "")
    {
      setFilteredTempArticles(articles.filter((article) => article.published_year === retrieve_param_object_from_id(publishedYears, selectedPublishedYear) 
                                                        && article.doi.toLowerCase().includes(selectedDoi.toLowerCase())))
    } 
    // Filter published year and title
    else if( selectedAuthor === ""
              && Number(selectedPublishedYear) > 0
              && selectedDoi === ""
              && selectedTitle !== "")
    {
      setFilteredTempArticles(articles.filter((article) => article.published_year === retrieve_param_object_from_id(publishedYears, selectedPublishedYear) 
                                                        && article.title.toLowerCase().includes(selectedTitle.toLowerCase())))
    } 
    // Filter doi and title
    else if( selectedAuthor === ""
              && Number(selectedPublishedYear) === 0
              && selectedDoi !== ""
              && selectedTitle !== "")
    {
      setFilteredTempArticles(articles.filter((article) => article.doi.toLowerCase().includes(selectedDoi.toLowerCase()) 
                                                        && article.title.toLowerCase().includes(selectedTitle.toLowerCase())))
    } 
    // Filter author and published year and doi
    else if( selectedAuthor !== "" 
              && Number(selectedPublishedYear) > 0
              && selectedDoi !== ""
              && selectedTitle === "")
    {
      setFilteredTempArticles(articles.filter((article) => article.author.toLowerCase().includes(selectedAuthor.toLowerCase()) 
                                                        && article.published_year === retrieve_param_object_from_id(publishedYears, selectedPublishedYear)
                                                        && article.doi.toLowerCase().includes(selectedDoi.toLowerCase())))
    } 
    // Filter author and published year and title
    else if( selectedAuthor !== "" 
              && Number(selectedPublishedYear) > 0
              && selectedDoi === ""
              && selectedTitle !== "")
    {
      setFilteredTempArticles(articles.filter((article) => article.author.toLowerCase().includes(selectedAuthor.toLowerCase()) 
                                                        && article.published_year === retrieve_param_object_from_id(publishedYears, selectedPublishedYear)
                                                        && article.title.toLowerCase().includes(selectedTitle.toLowerCase())))
    } 
    // Filter author and doi and title
    else if( selectedAuthor !== ""
              && Number(selectedPublishedYear) === 0
              && selectedDoi !== ""
              && selectedTitle !== "")
    {
      setFilteredTempArticles(articles.filter((article) => article.author.toLowerCase().includes(selectedAuthor.toLowerCase()) 
                                                        && article.doi.toLowerCase().includes(selectedDoi.toLowerCase())
                                                        && article.title.toLowerCase().includes(selectedTitle.toLowerCase())))
    } 
    // Filter published year and doi and title
    else if( selectedAuthor === "" 
              && Number(selectedPublishedYear) > 0
              && selectedDoi !== ""
              && selectedTitle !== "")
    {
      setFilteredTempArticles(articles.filter((article) => article.published_year === retrieve_param_object_from_id(publishedYears, selectedPublishedYear)
                                                        && article.doi.toLowerCase().includes(selectedDoi.toLowerCase())
                                                        && article.title.toLowerCase().includes(selectedTitle.toLowerCase())))
    } 
    // Filter author and published year and doi and title
    else if( selectedAuthor !== "" 
              && Number(selectedPublishedYear) > 0
              && selectedDoi !== ""
              && selectedTitle !== "")
    {
      setFilteredTempArticles(articles.filter((article) => article.author.toLowerCase().includes(selectedAuthor.toLowerCase()) 
                                                        && article.published_year === retrieve_param_object_from_id(publishedYears, selectedPublishedYear)
                                                        && article.doi.toLowerCase().includes(selectedDoi.toLowerCase())
                                                        && article.title.toLowerCase().includes(selectedTitle.toLowerCase())))
    } 
    // No filter
    else
    {
      setFilteredTempArticles(articles)
    }
  }

  function updateFilteredArticlesByKeywords() {
    // Filter keywords in keywords field and abstract field
    if(selectedKeywords !== "")
    {
      setFilteredArticles(filteredTempArticles.filter((article) => article.abstract.toLowerCase().includes(selectedKeywords.toLowerCase())
                                                                   || article.keywords.toLowerCase().includes(selectedKeywords.toLowerCase())))
    } 
    // No filter
    else
    {
      setFilteredArticles(filteredTempArticles)
    }
  }

  const filterMissingKeywords = (event) => {
      event.preventDefault();
      setFilteredArticles(articles.filter((article) => !article.abstract?.trim() && !article.keywords?.trim()))
  }

  const filterReset = (event) => {
      event.preventDefault();
      setFilteredArticles(articles)
      setSelectedDoi("")
      setSelectedTitle("")
      setSelectedAuthor("")
      setSelectedPublishedYear(0)
      setSelectedKeywords("")
  }

  function handleFile (event) {
    const file = event.target.files[0];
    setFile(file);
  };

  const handleImport = (event) => {
    event.preventDefault();
    if(file === null || file === ''){
      setFileError(true)
    }
    else
    {   
      setFileError(false)
    }
    if(fileError === false){
      importFile(file)
    }
  };

  async function importFile(file) {
    // Token
    const token = "Bearer " + authToken
    // Création du Form Data
    let form_data = new FormData();
    form_data.append("file", file, file.name);
    // Perform the import
    const response = await fetch(path + "/api/bo-scar/import/", 
        {
            method: 'POST',
            credentials: 'include',
            mode: 'cors',
            headers: {
                accept: 'application/json',
                //'Content-Type': 'multipart/form-data',  cette ligne ne doit pas être utilisée avec FormData sinon erreur
                Authorization: token,
            },
            body: form_data
        }
    );
    setStatusImport(response["status"])
  }

  const handleExport = (event) => {
    event.preventDefault();
    exportToBibTex();
  };

  function exportToBibTex() {
    var merged_content = ""
    filteredArticles.map((article) => (
      merged_content += `
        @article{${article['author'].split(',')[0]}${article['published_year']},
        abstract = {${article['abstract']}},
        author = {${article['author']}},
        doi = {${article['doi']}},
        keywords = {${article['keywords']}},
        title = {${article['title']}},
        year = {${article['published_year']}}
        }
      `)
    )
    const blob = new Blob([merged_content], { type: "text/plain" });
    saveAs(blob, "bibliographie.bib");
  }

  async function updateLastDateImport(date) {
    // Token
    const token = "Bearer " + authToken
    // Update date
    await fetch(path + "/api/last-import/", 
      {
        method: 'PATCH',
        credentials: 'include',
        mode: 'cors',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
        },
        body: JSON.stringify({
            "new_mendeley_last_import_date": date
        })
      }
    );
    setLastDateImport(date)
  }


  // Define useEffect hooks
  useEffect(() => {
    if(authStatus === 200)
    {
      setDataLoading(true)
      retrieveData()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if(doiError === false && titleError === false && authorError === false && publishedYearError === false && keywordsError === false && isFormClicked === true){
      sendData(doi, title, author, publishedYear, keywords)
    } else {
      setFormClicked(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doiError, titleError, authorError, publishedYearError, isFormClicked])

  useEffect(() => {
    updateFilteredArticles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articles, selectedDoi, selectedTitle, selectedAuthor, selectedPublishedYear])

  useEffect(() => {
    updateFilteredArticlesByKeywords();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTempArticles, selectedKeywords])

  useEffect(() => {
    if(statusImport === 200)
    {
      retrieveData()
      //setLastDateImport(dayjs().format('DD/MM/YYYY'))
      updateLastDateImport(dayjs().format('DD/MM/YYYY'))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusImport])

  useEffect(() => {
      // Bloquer la fermeture d'onglet/rafraîchissement
      const handleBeforeUnload = (e) => {
      if (shouldBlock === true) {
          // Standard pour afficher une confirmation (les navigateurs modernes gèrent le message)
          e.preventDefault();
          e.returnValue = "";
          // Certains navigateurs nécessitent cette ligne pour afficher la confirmation
          return (e.returnValue = "Cette page vous demande de confirmer sa fermeture ; des données que vous avez saisies pourraient ne pas être enregistrées.");
      }
      }; 

      window.addEventListener("beforeunload", handleBeforeUnload);

      // Nettoyer l'écouteur quand le composant est démonté
      return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldBlock]);

  useEffect(() => {
      if (blocker.state === "blocked") {
          const confirmed = window.confirm(
              "Cette page vous demande de confirmer sa fermeture ; des données que vous avez saisies pourraient ne pas être enregistrées."
          );

          if (confirmed) {
              blocker.proceed();
          } else {
              blocker.reset();
          }
      }
  }, [blocker]);


  // Render component
  return (
    <div>
      <TabRD tabNumber={4}/>
      <div className={(screenWidth > 650) ? 'App' : 'App-ss'}>
        <Container>
          {isDataLoading ? (
            <p>Chargement des données</p>
          ) : (
            (statusArticles === 200) ? (
              <div>
                <h1>Articles scientifiques</h1>
                <div className={(screenWidth > 650) ? 'grid-1-3' : null}>
                  {/*New*/}
                  <div className='flex-column'>
                    <div className='link' onClick={handleClick}>
                      <img src={AddIcon} alt='add_item_icon' className='link-img' />
                      <div className='link-text'>Article</div>
                    </div>
                    {(isCreation === true) ? (
                      <div className='flex-rows'>
                        <h3>Nouvel article</h3>
                        <form onSubmit={handleSubmit} onReset={handleReset} className='flex-column'>
                          <div className='flex-column form-center'>
                            <InputFormSide error={doiError} type="text" label="DOI" value={doi} setValue={setDoi}/>
                            <InputFormSide error={titleError} type="text" label="Titre" value={title} setValue={setTitle}/>
                            <InputFormSide error={authorError} type="text" label="Auteur" value={author} setValue={setAuthor}/>
                            <InputFormSide error={publishedYearError} type="text" label="Année de publication" value={publishedYear} setValue={setPublishedYear}/>
                            <InputFormSide error={keywordsError} type="text" label="Mots clés" value={keywords} setValue={setKeywords}/>
                          </div>
                          <div className='flex-row form-center'>
                            <button type="reset" className='button'>Annuler</button>
                            <button type="submit" className='button'>Valider</button>
                          </div>
                        </form> 
                      </div>
                    ) : (
                      <div></div>
                    )}
                    {/*Filtering*/}
                    <h3>Filtres</h3>
                    <TextFilter label="DOI" value={selectedDoi} setValue={setSelectedDoi}></TextFilter>
                    <TextFilter label="Title" value={selectedTitle} setValue={setSelectedTitle}></TextFilter>
                    <TextFilter label="Auteur" value={selectedAuthor} setValue={setSelectedAuthor}></TextFilter>
                    <ListFilter labelText='Année de publication' choices={publishedYears.sort((a, b) => (a.value > b.value)?1:-1)} choiceParam={'value'} selectedItem={selectedPublishedYear} setSelectedItem={setSelectedPublishedYear} subfilter={false}/>
                    <TextFilter label="Mot clé" value={selectedKeywords} setValue={setSelectedKeywords}></TextFilter>
                    <div className='grid-2-2-1'>
                      <button onClick={filterMissingKeywords} className='button-secondary clickable'>Mots clés vides</button>
                      <button onClick={filterReset} className='button-secondary clickable'>Sans filtres</button>
                      <div/>
                    </div>
                    {/*Import and export*/}
                    <h3>Import</h3>
                    <p className='item-text'>Dernier import : {lastDateImport}</p>
                    <form onSubmit={handleImport} className='flex-column'>
                        <div className='flex-column'>
                          <FileForm error={fileError} label="Importer depuis un fichier Mendeley" onChangeFunc={handleFile} currentValue={null}/>
                        </div>
                        <div className='flex-row form-center'>
                          <button type="submit" className='button'>Valider</button>
                          <div/>
                        </div>
                    </form> 
                    <h3>Export</h3>
                    <div onClick={handleExport} className='link clickable'>
                      <img src={FileIcon} alt='file_icon' className='link-img' />
                      <div className='link-text'>Exporter au format BibTex</div>
                    </div>
                  </div>
                  {(screenWidth > 650) ? null : <Divider/>}
                  {/*Scientific articles list*/}
                  <div className='scroll-list-article'>
                    <h2 className='subtitle'>Nombre d'articles scientifiques : {filteredArticles.length}</h2>
                    {(filteredArticles && filteredArticles.length !== 0) ? (
                      filteredArticles.sort((a, b) => (a.doi > b.doi)?1:-1).map((article) => (
                        <Item key={article['id']} id={article['id']}>
                          <div className='grid-10-1'>
                            <h3 className='item-text'>{article['title']}</h3> 
                            <div className='flex-row justify-end'>
                              <div/>
                              {<img src={EditIcon} alt='edit_item_icon' onClick={(e) => handleEditIcon(e, article) } className='link-img link-icon' />}
                              { (currentUser["role_name"] === "AT_ADMIN") ? (
                                  <img src={DeleteIcon} alt='delete_item_icon' onClick={(e) => handleRemoveIcon(e, article['id']) } className='link-img'/>
                                ) : (
                                  <p/>
                                )
                              }
                            </div>
                          </div>
                          <div className='grid-1-1'>
                            <p className='item-text'><strong>DOI :</strong>{article['doi']}</p>
                            <p className='item-text'><strong>Année : </strong>{article['published_year']}</p>
                          </div>
                          <p className='item-text'><strong>Auteurs : </strong>{article['author']}</p>
                          <p className='item-text'><strong>Résumé : </strong>{article['abstract']}</p>
                          <p className='item-text'><strong>Mots clés : </strong>{article['keywords']}</p>
                          {(isUpdate === true && article['id'] === idUpdated) ? (
                            <form onSubmit={updateSubmit} onReset={updateReset} className='flex-row justify-end'>
                                <input className='form-input' type="text" value={newKeywords} onChange={(e) => setNewKeywords(e.target.value)}/>
                                <button type="reset" className='button'>Annuler</button>
                                <button type="submit" className='button'>Valider</button>
                            </form> 
                          ) : (
                            <div></div>
                          )}
                        </Item>
                      ))
                    ) : (
                      <p>Aucun article actuellement. Veuillez en créer un !</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <h1>Connexion failed</h1>
            )
          )}
        </Container>
      </div>
    </div>
  );
}

export default ScientificArticleList;
